<template>
  <div class="detail-page">
    <router-link
      :to="getBackLink()"
      class="back-link"
      :title="`Kembali ke Daftar ${formatTipe(tipeTokoh)}`"
      aria-label="Kembali ke daftar"
    >
      <span class="back-icon" aria-hidden="true">&larr;</span>
      <span>Kembali ke Daftar {{ formatTipe(tipeTokoh) }}</span>
    </router-link>

    <div v-if="loading" class="state-box">Memuat data individu...</div>
    <div v-else-if="error" class="state-box error-box">{{ error }}</div>
    <div v-else class="detail-stack">
      <section class="profile-card">
        <div class="image-wrap">
          <img
            v-if="gambarUrl"
            :src="gambarUrl"
            :alt="`Gambar dari ${formatName(namaTokoh)}`"
            class="tokoh-image"
          />
          <div v-else class="image-empty">Gambar belum tersedia</div>
        </div>

        <div class="identity-wrap">
          <p class="identity-label">Profil Individu</p>
          <h1 class="detail-title">{{ formatName(namaTokoh) }}</h1>

          <div class="type-chip-wrap">
            <span class="type-chip">{{ formatTipe(tipeTokoh) || 'Tidak diketahui' }}</span>
          </div>

          <p class="identity-note">
            Informasi berikut memuat relasi ontologi dan detail tambahan terkait individu pada basis pengetahuan Wayang Purwa.
          </p>
        </div>
      </section>

      <section v-if="familyRelations.length" class="info-card graph-card">
        <div class="graph-heading">
          <div>
            <p class="identity-label">Visualisasi Relasi</p>
            <h2 class="section-title">Keluarga</h2>
          </div>
          <span class="graph-hint">Klik nama untuk melihat detail</span>
        </div>
        <div ref="familyGraph" class="family-graph" aria-label="Graph relasi keluarga"></div>
      </section>

      <section v-if="objectProperties.length" class="info-card">
        <h2 class="section-title">Atribut dan Relasi</h2>
        <ul class="property-grid">
          <li v-for="(obj, index) in objectProperties" :key="`obj-${index}`" class="property-item">
            <span class="property-label">{{ formatProperty(obj.property) }}</span>
            <router-link
              v-if="obj.isIndividual"
              :to="`/detail/${encodeURIComponent(obj.value)}`"
              class="property-link"
            >
              {{ formatName(obj.value) }}
            </router-link>
            <span v-else class="property-value">{{ obj.value }}</span>
          </li>
        </ul>
      </section>

      <section v-if="dataProperties.length" class="info-card">
        <h2 class="section-title">Detail Tambahan</h2>
        <ul class="property-grid">
          <li v-for="(data, index) in dataProperties" :key="`data-${index}`" class="property-item">
            <span class="property-label">{{ formatProperty(data.property) }}</span>
            <span class="property-value">{{ data.value }}</span>
          </li>
        </ul>
      </section>

      <section v-if="!objectProperties.length && !dataProperties.length" class="state-box">
        Detail properti untuk individu ini belum tersedia.
      </section>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { Network } from 'vis-network/standalone'

const familyPropertyNames = [
  'hasBrother',
  'hasSon',
  'hasDaughter',
  'hasFather',
  'hasMother',
  'hasWife'
]

export default {
  name: 'IndividualDetail',
  data() {
    return {
      namaTokoh: '',
      tipeTokoh: '',
      objectProperties: [],
      familyRelations: [],
      dataProperties: [],
      gambarUrl: '',
      loading: true,
      error: null,
      familyNetwork: null
    }
  },
  mounted() {
    this.fetchData()
  },
  watch: {
    '$route.params.nama': function () {
      this.fetchData()
    }
  },
  methods: {
    async fetchData() {
      this.loading = true
      this.error = null
      this.namaTokoh = decodeURIComponent(this.$route.params.nama)

      this.tipeTokoh = ''
      this.objectProperties = []
      this.familyRelations = []
      this.dataProperties = []
      this.destroyFamilyGraph()

      const endpoint = 'https://app.alunalun.info/fuseki/wayangpurwa/sparql'

      try {
        const tipeQuery = `
          PREFIX owl: <http://www.w3.org/2002/07/owl#>
          PREFIX rdf: <http://www.w3.org/1999/02/22-rdf-syntax-ns#>
          PREFIX wp: <http://ukdw.ac.id/ontologi/wayangpurwa#>
          PREFIX ontodebug: <http://ainf.aau.at/ontodebug#>
          PREFIX wayangpurwa: <http://ontology/wayangpurwa#>
          PREFIX ontology: <http://a.com/ontology#>

          SELECT ?tipe WHERE {
            wp:${decodeURIComponent(this.namaTokoh)} rdf:type ?tipe .
            FILTER(?tipe != owl:NamedIndividual)
          }
        `
        const tipeRes = await axios.get(endpoint, {
          params: { query: tipeQuery, format: 'json' }
        })
        const tipeBindings = tipeRes.data.results.bindings
        if (tipeBindings.length) {
          this.tipeTokoh = tipeBindings[0].tipe.value.split('#').pop()
        }

        const propQuery = `
          PREFIX rdf: <http://www.w3.org/1999/02/22-rdf-syntax-ns#>
          PREFIX wp: <http://ukdw.ac.id/ontologi/wayangpurwa#>
          PREFIX ontodebug: <http://ainf.aau.at/ontodebug#>
          PREFIX wayangpurwa: <http://ontology/wayangpurwa#>
          PREFIX ontology: <http://a.com/ontology#>

          SELECT DISTINCT ?property ?value WHERE {
            wp:${this.namaTokoh} ?property ?value .
            FILTER(?property != rdf:type)
          }
        `
        const propRes = await axios.get(endpoint, {
          params: { query: propQuery, format: 'json' }
        })
        const propBindings = propRes.data.results.bindings

        const objectProps = []
        const familyRelations = []
        const dataProps = []

        propBindings.forEach(b => {
          const propName = b.property.value.split('#').pop()
          const val = b.value.type === 'uri'
            ? b.value.value.split('#').pop()
            : b.value.value

          if (b.value.type === 'uri') {
            const relation = { property: propName, value: val, isIndividual: true }
            objectProps.push(relation)
            if (familyPropertyNames.includes(propName)) {
              familyRelations.push(relation)
            }
          } else {
            dataProps.push({ property: propName, value: val })
          }
        })

        this.objectProperties = objectProps
        this.familyRelations = familyRelations
        this.dataProperties = dataProps

        this.gambarUrl = this.getGambarUrl(this.namaTokoh)
      } catch (err) {
        this.error = 'Gagal memuat detail individu.'
        console.error(err)
      } finally {
        this.loading = false
        await this.$nextTick()
        this.drawFamilyGraph()
      }
    },
    drawFamilyGraph() {
      if (!this.$refs.familyGraph || !this.familyRelations.length) return

      const nodes = [
        {
          id: this.namaTokoh,
          label: this.formatName(this.namaTokoh),
          color: { background: '#b45309', border: '#78350f' },
          font: { color: '#ffffff', size: 16, face: 'Plus Jakarta Sans' },
          size: 28
        }
      ]
      const nodeIds = new Set([this.namaTokoh])
      const edges = []

      this.familyRelations.forEach((relation, index) => {
        if (!nodeIds.has(relation.value)) {
          nodeIds.add(relation.value)
          nodes.push({
            id: relation.value,
            label: this.formatName(relation.value),
            color: { background: '#fef3c7', border: '#d97706' },
            font: { color: '#451a03', size: 14, face: 'Plus Jakarta Sans' },
            size: 22
          })
        }

        edges.push({
          id: `family-${index}`,
          from: this.namaTokoh,
          to: relation.value,
          arrows: 'to',
          label: this.formatProperty(relation.property),
          color: { color: '#b45309', highlight: '#78350f' },
          font: { align: 'top', size: 12, face: 'Plus Jakarta Sans' }
        })
      })

      this.familyNetwork = new Network(this.$refs.familyGraph, { nodes, edges }, {
        autoResize: true,
        interaction: { hover: true, navigationButtons: true, tooltipDelay: 100 },
        nodes: { shape: 'dot', borderWidth: 2, shadow: true },
        edges: { smooth: { type: 'curvedCW', roundness: 0.2 } },
        physics: {
          solver: 'forceAtlas2Based',
          forceAtlas2Based: { gravitationalConstant: -40, springLength: 170, springConstant: 0.06 },
          stabilization: { iterations: 180 }
        }
      })

      this.familyNetwork.on('click', ({ nodes: selectedNodes }) => {
        const selectedName = selectedNodes[0]
        if (selectedName && selectedName !== this.namaTokoh) {
          this.$router.push(`/detail/${encodeURIComponent(selectedName)}`)
        }
      })
    },
    destroyFamilyGraph() {
      if (this.familyNetwork) {
        this.familyNetwork.destroy()
        this.familyNetwork = null
      }
    },
    getGambarUrl(nama) {
      try {
        return require(`@/assets/wayang/${nama}.jpg`)
      } catch (e) {
        console.warn(`Gambar untuk ${nama} tidak ditemukan.`)
        return null
      }
    },
    formatName(name) {
      return name
        .split('_')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
    },
    formatTipe(tipe) {
      return tipe.replace(/([a-z])([A-Z])/g, '$1 $2')
    },
    formatProperty(prop) {
      return prop
        .replace(/^has/, '')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/^./, c => c.toUpperCase())
    },
    getBackLink() {
      const tipe = this.tipeTokoh.toLowerCase()
      if (tipe.includes('senjata') || tipe.includes('pusaka')) {
        return '/pusaka'
      } else if (tipe.includes('kesaktian')) {
        return '/kesaktian'
      } else if (
        ['kahyangan', 'kerajaan', 'kerajaanmanusia', 'kerajaandewa', 'kerajaankera', 'kerajaanmakhlukhalus', 
        'kerajaanraksasa', 'kerajaansiluman','pertapaan', 'laut', 'sungai', 'keputren', 'kesatriyan', 'padhukuhan']
          .includes(tipe)
      ) {
        return '/tempat-asal'
      } else {
        return '/tokoh'
      }
    }
  },
  beforeUnmount() {
    this.destroyFamilyGraph()
  }
}
</script>

<style scoped>
.detail-page {
  max-width: 1100px;
  margin: 0 auto;
  padding: 32px;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
  padding: 8px 12px;
  border-radius: 999px;
  text-decoration: none;
  color: #1e40af;
  background: linear-gradient(135deg, #dbeafe, #eff6ff);
  border: 1px solid #bfdbfe;
  box-shadow: 0 6px 16px rgba(30, 64, 175, 0.12);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.back-link:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(30, 64, 175, 0.16);
}

.back-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: #1d4ed8;
  color: #ffffff;
  font-weight: 700;
  line-height: 1;
}

.detail-stack {
  display: grid;
  gap: 16px;
}

.profile-card {
  display: grid;
  grid-template-columns: minmax(240px, 280px) 1fr;
  gap: 20px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 18px;
  background: #ffffff;
}

.image-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
}

.tokoh-image {
  width: 100%;
  max-width: 260px;
  border: 2px solid #dbeafe;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  object-fit: cover;
}

.image-empty {
  width: 100%;
  min-height: 260px;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  background: #f8fafc;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 14px;
}

.identity-wrap {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.identity-label {
  margin: 0;
  color: #64748b;
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 0.08em;
}

.detail-title {
  margin: 8px 0 12px;
  font-size: clamp(1.6rem, 3vw, 2.25rem);
  color: #0f172a;
}

.type-chip-wrap {
  margin-bottom: 12px;
}

.type-chip {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  color: #1e3a8a;
  background: #e0e7ff;
  border: 1px solid #c7d2fe;
}

.identity-note {
  margin: 0;
  color: #475569;
  line-height: 1.7;
}

.info-card {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 18px;
  background: #ffffff;
}

.section-title {
  margin: 0 0 12px;
  font-size: 1.2rem;
  color: #0f172a;
}

.graph-card {
  overflow: hidden;
}

.graph-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.graph-hint {
  color: #64748b;
  font-size: 13px;
}

.family-graph {
  height: 420px;
  margin-top: 4px;
  border: 1px solid #fde68a;
  border-radius: 12px;
  background: linear-gradient(135deg, #fffbeb, #fff7ed);
}

.property-grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 10px;
}

.property-item {
  display: grid;
  grid-template-columns: minmax(160px, 220px) 1fr;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
}

.property-label {
  font-weight: 700;
  color: #334155;
}

.property-value {
  color: #1e293b;
  word-break: break-word;
}

.property-link {
  color: #1d4ed8;
  text-decoration: none;
  font-weight: 600;
  word-break: break-word;
}

.property-link:hover {
  color: #1e40af;
  text-decoration: underline;
}

.state-box {
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  padding: 18px;
  background: #f8fafc;
  color: #475569;
}

.error-box {
  border-color: #fca5a5;
  background: #fef2f2;
  color: #991b1b;
}

@media (max-width: 900px) {
  .detail-page {
    padding: 20px;
  }

  .profile-card {
    grid-template-columns: 1fr;
  }

  .property-item {
    grid-template-columns: 1fr;
  }

  .graph-heading {
    flex-direction: column;
    gap: 4px;
  }

  .family-graph {
    height: 360px;
  }
}
</style>
